import React, {FC, useState, useEffect, useRef, useLayoutEffect} from 'react';
import {Dayjs} from 'dayjs';
import styled from 'styled-components';
import {DateAndTimePicker} from './DateAndTimePicker';
import {LocalTimeDisplay} from '../../businessCommon/localTimeDisplay/LocalTimeDisplay';

const DateAndTimeRangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const DateInputsRow = styled.div<{ $dateOnFullRow?: boolean; $isWrapped?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.$dateOnFullRow ? '24px' : '8px'};
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const DateInputWrapper = styled.div<{ $isWrapped?: boolean; $isSecond?: boolean }>`
  flex: 1;
  min-width: 200px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  ${props => props.$isWrapped && props.$isSecond && `
    flex-basis: 100%;
    width: 100%;
  `}
`;

const LocalTimeWrapper = styled.div`
  margin-top: 2px;
  display: flex;
  justify-content: flex-end;
  min-height: 18px;
`;

const RangeSeparator = styled.div<{isVisible?: boolean; $dateOnFullRow?: boolean; $isWrapped?: boolean}>`
  display: ${props => props.$isWrapped ? 'none' : 'flex'};
  align-items: center;
  align-self: flex-start;
  font-size: 14px;
  color: #666;
  font-weight: 500;
  padding: 0 ${props => props.$dateOnFullRow ? '8px' : '4px'};
  min-width: ${props => props.$dateOnFullRow ? '24px' : '20px'};
  justify-content: center;
  flex-shrink: 0;
  margin-top: 12px;
`;

interface IDateAndTimeRangePickerProps {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => void;
  placeholder?: [string, string];
  disabledDate?: (current: Dayjs) => boolean;
  inputReadOnly?: boolean;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  format?: string;
  dateOnFullRow?: boolean;
  airportFromOffset?: number | null; // timezone offset в минутах для аэропорта "from"
  airportToOffset?: number | null; // timezone offset в минутах для аэропорта "to"
  containerHeight?: string | number;
  [key: string]: any;
}

export const DateAndTimeRangePicker: FC<IDateAndTimeRangePickerProps> = ({
  value,
  onChange,
  placeholder = ['', ''],
  disabledDate,
  inputReadOnly = true,
  placement = "bottomLeft",
  getPopupContainer,
  format = "YYYY-MM-DD",
  dateOnFullRow = false,
  airportFromOffset,
  airportToOffset,
  containerHeight,
  ...props
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(value?.[0] || null);
  const [endDate, setEndDate] = useState<Dayjs | null>(value?.[1] || null);
  const [isWrapped, setIsWrapped] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const firstPickerRef = useRef<HTMLDivElement>(null);
  const separatorRef = useRef<HTMLDivElement>(null);
  const secondPickerRef = useRef<HTMLDivElement>(null);

  // Инициализация дат из value
  useEffect(() => {
    if (value) {
      setStartDate(value[0]);
      setEndDate(value[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [value]);

  // Обработка изменения даты начала
  const handleStartDateChange = (date: Dayjs | null, dateString: string) => {
    // Если дата совпадает с датой окончания и есть время, проверяем время
    let adjustedEndDate = endDate;
    if (date && endDate && date.isSame(endDate, 'day')) {
      // Если дата одинаковая и время начала больше времени окончания, корректируем время окончания
      if (date && !date.isSame(date.startOf('day')) && endDate && !endDate.isSame(endDate.startOf('day'))) {
        const startTime = date.hour() * 60 + date.minute();
        const endTime = endDate.hour() * 60 + endDate.minute();
        if (startTime > endTime) {
          // Устанавливаем время окончания равным времени начала
          adjustedEndDate = endDate.hour(date.hour()).minute(date.minute()).second(date.second());
        }
      }
    }
    
    setStartDate(date);
    
    // Если оба поля были пустые и мы вводим дату в левое поле, копируем её в правое
    if (date && !adjustedEndDate && !startDate) {
      setEndDate(date);
      const newValue: [Dayjs, Dayjs] = [date, date];
      if (onChange) {
        // Форматируем дату без времени если это только дата
        const endDateString = date.isSame(date.startOf('day')) ? date.format(format || 'YYYY-MM-DD') : date.format('YYYY-MM-DD HH:mm');
        onChange(newValue, [dateString, endDateString]);
      }
      return;
    }
    
    // Если скорректировали дату окончания, обновляем её
    if (adjustedEndDate && adjustedEndDate !== endDate) {
      setEndDate(adjustedEndDate);
    }
    
    // Обновляем общее значение
    const newValue: [Dayjs | null, Dayjs | null] = [date, adjustedEndDate];
    if (onChange) {
      // Форматируем дату окончания в зависимости от наличия времени
      const endDateString = adjustedEndDate 
        ? (adjustedEndDate.isSame(adjustedEndDate.startOf('day')) ? adjustedEndDate.format(format || 'YYYY-MM-DD') : adjustedEndDate.format('YYYY-MM-DD HH:mm'))
        : '';
      onChange(newValue, [dateString, endDateString]);
    }
  };

  // Обработка изменения даты окончания
  const handleEndDateChange = (date: Dayjs | null, dateString: string) => {
    // Если дата совпадает с датой начала и есть время в дате начала, проверяем время
    if (date && startDate && date.isSame(startDate, 'day')) {
      // Если время в date раньше времени в startDate, корректируем его
      if (startDate && !startDate.isSame(startDate.startOf('day'))) {
        const startTime = startDate.hour() * 60 + startDate.minute();
        const endTime = date.hour() * 60 + date.minute();
        if (endTime < startTime) {
          // Устанавливаем время окончания равным времени начала
          date = date.hour(startDate.hour()).minute(startDate.minute()).second(startDate.second());
          dateString = date.format('YYYY-MM-DD HH:mm');
        }
      }
    }
    
    setEndDate(date);
    
    // Если оба поля были пустые и мы вводим дату в правое поле, копируем её в левое
    if (date && !startDate && !endDate) {
      setStartDate(date);
      const newValue: [Dayjs, Dayjs] = [date, date];
      if (onChange) {
        // Форматируем дату без времени если это только дата
        const startDateString = date.isSame(date.startOf('day')) ? date.format(format || 'YYYY-MM-DD') : date.format('YYYY-MM-DD HH:mm');
        onChange(newValue, [startDateString, dateString]);
      }
      return;
    }
    
    // Обновляем общее значение
    const newValue: [Dayjs | null, Dayjs | null] = [startDate, date];
    if (onChange) {
      // Форматируем дату начала в зависимости от наличия времени
      const startDateString = startDate 
        ? (startDate.isSame(startDate.startOf('day')) ? startDate.format(format || 'YYYY-MM-DD') : startDate.format('YYYY-MM-DD HH:mm'))
        : '';
      onChange(newValue, [startDateString, dateString]);
    }
  };

  // Функция для проверки, должна ли дата быть заблокирована
  const getDisabledDateForStart = (current: Dayjs) => {
    if (disabledDate) {
      return disabledDate(current);
    }
    // Если есть дата окончания, блокируем даты после неё
    if (endDate) {
      return current.isAfter(endDate, 'day');
    }
    return false;
  };

  const getDisabledDateForEnd = (current: Dayjs) => {
    if (disabledDate) {
      return disabledDate(current);
    }
    // Если есть дата начала, блокируем даты до неё
    if (startDate) {
      return current.isBefore(startDate, 'day');
    }
    return false;
  };

  // Вычисляем minTime для поля окончания (если дата одинаковая)
  const getMinTimeForEnd = (): Dayjs | null => {
    // Проверяем, совпадает ли текущая дата окончания (или дата начала, если окончания нет) с датой начала
    const currentEndDate = endDate || startDate;
    if (startDate && currentEndDate && startDate.isSame(currentEndDate, 'day')) {
      // Если дата одинаковая, возвращаем время из startDate
      if (!startDate.isSame(startDate.startOf('day'))) {
        return startDate;
      }
    }
    return null;
  };


  // Проверка, произошел ли перенос строк
  useLayoutEffect(() => {
    const checkWrap = () => {
      if (firstPickerRef.current && secondPickerRef.current && rowRef.current && separatorRef.current) {
        const rowRect = rowRef.current.getBoundingClientRect();
        const containerWidth = rowRect.width;

        // Минимальные ширины элементов
        const firstPickerMinWidth = 200; // min-width из DateInputWrapper
        const secondPickerMinWidth = 200; // min-width из DateInputWrapper
        const separatorMinWidth = dateOnFullRow ? 24 : 20; // min-width из RangeSeparator
        const gap = dateOnFullRow ? 24 : 8; // gap между элементами
        
        // Суммарная минимальная ширина всех элементов
        const totalMinWidth = firstPickerMinWidth + separatorMinWidth + secondPickerMinWidth + gap * 2;
        
        // Проверяем, помещаются ли элементы с минимальной шириной в один ряд
        const needsWrap = totalMinWidth > containerWidth;

        setIsWrapped(needsWrap);
      }
    };

    checkWrap();

    // Добавляем обработчик изменения размера окна
    const resizeObserver = new ResizeObserver(checkWrap);
    if (rowRef.current) {
      resizeObserver.observe(rowRef.current);
    }
    if (firstPickerRef.current) {
      resizeObserver.observe(firstPickerRef.current);
    }
    if (secondPickerRef.current) {
      resizeObserver.observe(secondPickerRef.current);
    }
    if (separatorRef.current) {
      resizeObserver.observe(separatorRef.current);
    }

    // Также проверяем при изменении размера окна
    window.addEventListener('resize', checkWrap);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkWrap);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dateOnFullRow from ref/resize, stable
  }, [startDate, endDate]);

  return (
    <DateAndTimeRangeWrapper>
      <DateInputsRow ref={rowRef} $dateOnFullRow={dateOnFullRow} $isWrapped={isWrapped}>
        <DateInputWrapper ref={firstPickerRef} $isWrapped={isWrapped} $isSecond={false}>
          <DateAndTimePicker
            value={startDate}
            onChange={handleStartDateChange}
            format={format}
            placeholder={placeholder[0]}
            disabledDate={getDisabledDateForStart}
            inputReadOnly={inputReadOnly}
            placement={placement}
            getPopupContainer={getPopupContainer}
            dateOnFullRow={dateOnFullRow}
            containerHeight={containerHeight}
            {...props}
          />
          <LocalTimeWrapper>
            <LocalTimeDisplay
              utcDate={startDate}
              airportOffset={airportFromOffset}
            />
          </LocalTimeWrapper>
        </DateInputWrapper>
        <RangeSeparator ref={separatorRef} isVisible={!isWrapped} $dateOnFullRow={dateOnFullRow} $isWrapped={isWrapped}>—</RangeSeparator>
        <DateInputWrapper ref={secondPickerRef} $isWrapped={isWrapped} $isSecond={true}>
          <DateAndTimePicker
            value={endDate}
            onChange={handleEndDateChange}
            format={format}
            placeholder={placeholder[1]}
            disabledDate={getDisabledDateForEnd}
            inputReadOnly={inputReadOnly}
            placement={placement}
            getPopupContainer={getPopupContainer}
            minTime={getMinTimeForEnd()}
            dateOnFullRow={dateOnFullRow}
            containerHeight={containerHeight}
            {...props}
          />
          <LocalTimeWrapper>
            <LocalTimeDisplay
              utcDate={endDate}
              airportOffset={airportToOffset}
            />
          </LocalTimeWrapper>
        </DateInputWrapper>
      </DateInputsRow>
    </DateAndTimeRangeWrapper>
  );
};
